import {
  CLIENT_NUTRITION_DEFAULT_PURPOSE,
  CLIENT_NUTRITION_STATUS_ACTIVE,
  CLIENT_NUTRITION_STATUS_ARCHIVED,
} from "../../Firestore/nutrition/nutritionPaths";
import { getNutritionPlanDisplayName } from "./nutritionPlanNameUtils";

function normalizePlanId(value) {
  return String(value ?? "").trim().toLowerCase();
}

export function normalizeClientNutritionPlanStatus(value) {
  return String(value ?? "").trim() === CLIENT_NUTRITION_STATUS_ARCHIVED
    ? CLIENT_NUTRITION_STATUS_ARCHIVED
    : CLIENT_NUTRITION_STATUS_ACTIVE;
}

function normalizePendingStatusMap(pendingStatusByPlanId = {}) {
  return Object.entries(
    pendingStatusByPlanId && typeof pendingStatusByPlanId === "object"
      ? pendingStatusByPlanId
      : {},
  ).reduce((accumulator, [planId, status]) => {
    const normalizedPlanId = normalizePlanId(planId);
    if (!normalizedPlanId) {
      return accumulator;
    }

    accumulator[normalizedPlanId] = normalizeClientNutritionPlanStatus(status);
    return accumulator;
  }, {});
}

function getComparableDateTimestamp(value) {
  const resolvedDate = value?.toDate?.() ?? value ?? 0;
  const timestamp = new Date(resolvedDate).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function getComparableDateDisplayDayKey(value) {
  const resolvedDate = value?.toDate?.() ?? value ?? null;
  const parsedDate = resolvedDate ? new Date(resolvedDate) : null;
  if (!(parsedDate instanceof Date) || !Number.isFinite(parsedDate.getTime())) {
    return "";
  }

  return `${parsedDate.getMonth() + 1}-${parsedDate.getDate()}`;
}

function normalizeSummaryTotalsForComparison(summaryTotals) {
  const source =
    summaryTotals && typeof summaryTotals === "object" ? summaryTotals : {};

  return {
    calories: Number(source.calories ?? 0) || 0,
    carbs: Number(source.carbs ?? 0) || 0,
    protein: Number(source.protein ?? 0) || 0,
    fats: Number(source.fats ?? 0) || 0,
    fiber: Number(source.fiber ?? 0) || 0,
  };
}

function areSummaryTotalsEqual(leftTotals, rightTotals) {
  const left = normalizeSummaryTotalsForComparison(leftTotals);
  const right = normalizeSummaryTotalsForComparison(rightTotals);

  return (
    left.calories === right.calories &&
    left.carbs === right.carbs &&
    left.protein === right.protein &&
    left.fats === right.fats &&
    left.fiber === right.fiber
  );
}

function getPlanEffectiveStatus(plan, normalizedPendingStatusByPlanId = {}) {
  const normalizedPlanId = normalizePlanId(plan?.id);
  if (
    normalizedPlanId &&
    Object.prototype.hasOwnProperty.call(
      normalizedPendingStatusByPlanId,
      normalizedPlanId,
    )
  ) {
    return normalizedPendingStatusByPlanId[normalizedPlanId];
  }

  return normalizeClientNutritionPlanStatus(plan?.status);
}

function hasVisiblePlanNameChange(currentPlan, nextPlan) {
  const currentName = String(currentPlan?.name ?? "");
  const nextName = String(nextPlan?.name ?? "");
  if (currentName !== nextName) {
    return true;
  }

  if (currentName || nextName) {
    return false;
  }

  return (
    getComparableDateDisplayDayKey(currentPlan?.createdAt) !==
    getComparableDateDisplayDayKey(nextPlan?.createdAt)
  );
}

/**
 * Root `summary_totals` can be merged from Firestore with only `calories` while the MobX
 * entity has full computed totals. Prefer entity totals when summary looks calorie-only but
 * the entity has macro grams.
 */
export function resolveClientNutritionPlanRowTotals(plan) {
  const summary = plan?.summary_totals;
  const entityTotals = plan?.planEntity?.totals;
  if (!summary && !entityTotals) {
    return {};
  }

  const sCal = Number(summary?.calories ?? 0) || 0;
  const sP = Number(summary?.protein ?? 0) || 0;
  const sC = Number(summary?.carbs ?? 0) || 0;
  const sF = Number(summary?.fats ?? 0) || 0;
  const eP = Number(entityTotals?.protein ?? 0) || 0;
  const eC = Number(entityTotals?.carbs ?? 0) || 0;
  const eF = Number(entityTotals?.fats ?? 0) || 0;

  const summaryMissingMacros =
    sCal > 0 && sP === 0 && sC === 0 && sF === 0 && (eP > 0 || eC > 0 || eF > 0);

  if (summaryMissingMacros && entityTotals && typeof entityTotals === "object") {
    return entityTotals;
  }

  return summary ?? entityTotals ?? {};
}

export function buildClientNutritionPlanListItem({
  planId,
  entity = null,
  rootRecord = null,
} = {}) {
  const normalizedPlanId = normalizePlanId(planId ?? entity?.id ?? rootRecord?.id);
  if (!normalizedPlanId) {
    return null;
  }

  return {
    id: normalizedPlanId,
    name: String(entity?.name ?? rootRecord?.name ?? "").trim(),
    updatedAt: entity?.updatedAt ?? rootRecord?.updatedAt ?? null,
    createdAt: entity?.createdAt ?? rootRecord?.createdAt ?? null,
    status: normalizeClientNutritionPlanStatus(rootRecord?.status),
    purpose:
      String(rootRecord?.purpose ?? "").trim() || CLIENT_NUTRITION_DEFAULT_PURPOSE,
    activated_at: rootRecord?.activated_at ?? null,
    deactivated_at: rootRecord?.deactivated_at ?? null,
    origin_type: String(rootRecord?.origin_type ?? "").trim() || "client_direct",
    origin_plan_id: String(rootRecord?.origin_plan_id ?? "").trim() || "",
    origin_plan_rev: rootRecord?.origin_plan_rev ?? null,
    summary_totals: rootRecord?.summary_totals ?? entity?.summary_totals ?? null,
    meal_count:
      typeof rootRecord?.meal_count === "number"
        ? rootRecord.meal_count
        : typeof entity?.meal_count === "number"
          ? entity.meal_count
          : 0,
    createdByMe: entity?.createdByMe !== false,
    hasUnsyncedChanges: entity?.hasUnsyncedChanges === true,
    syncStatus: entity?.syncStatus ?? null,
    isDraft:
      entity != null &&
      Number(entity?._rev ?? 0) === 0 &&
      entity?.serverBase == null,
    planEntity: entity ?? null,
    rootRecord: rootRecord ?? null,
  };
}

export function hasClientNutritionPlanListItemVisibleChanges(
  currentPlan,
  nextPlan,
) {
  if (!currentPlan || !nextPlan) {
    return true;
  }

  // This comparator must stay aligned with the fields rendered by
  // ClientNutritionPlanRowCard. If that card starts showing another field,
  // update this list and its regression tests together.
  return (
    String(currentPlan.id ?? "") !== String(nextPlan.id ?? "") ||
    hasVisiblePlanNameChange(currentPlan, nextPlan) ||
    getComparableDateDisplayDayKey(currentPlan.updatedAt) !==
      getComparableDateDisplayDayKey(nextPlan.updatedAt) ||
    !areSummaryTotalsEqual(currentPlan.summary_totals, nextPlan.summary_totals)
  );
}

export function reconcileClientNutritionPlanListItem(currentPlan, nextPlan) {
  if (!currentPlan) {
    return nextPlan;
  }

  if (hasClientNutritionPlanListItemVisibleChanges(currentPlan, nextPlan)) {
    return nextPlan;
  }

  Object.assign(currentPlan, nextPlan);
  return currentPlan;
}

export function sortClientNutritionPlanListItems(plans, pendingStatusByPlanId = {}) {
  const normalizedPendingStatusByPlanId = normalizePendingStatusMap(
    pendingStatusByPlanId,
  );

  return [...(Array.isArray(plans) ? plans : [])].sort((leftPlan, rightPlan) => {
    const leftIsArchived =
      getPlanEffectiveStatus(leftPlan, normalizedPendingStatusByPlanId) ===
      CLIENT_NUTRITION_STATUS_ARCHIVED;
    const rightIsArchived =
      getPlanEffectiveStatus(rightPlan, normalizedPendingStatusByPlanId) ===
      CLIENT_NUTRITION_STATUS_ARCHIVED;

    if (leftIsArchived !== rightIsArchived) {
      return leftIsArchived ? 1 : -1;
    }

    const leftDate = getComparableDateTimestamp(
      leftPlan?.updatedAt ??
        leftPlan?.activated_at ??
        leftPlan?.createdAt ??
        leftPlan?.deployedAt,
    );
    const rightDate = getComparableDateTimestamp(
      rightPlan?.updatedAt ??
        rightPlan?.activated_at ??
        rightPlan?.createdAt ??
        rightPlan?.deployedAt,
    );

    if (rightDate !== leftDate) {
      return rightDate - leftDate;
    }

    return String(leftPlan?.name ?? "").localeCompare(
      String(rightPlan?.name ?? ""),
      undefined,
      { sensitivity: "base" },
    );
  });
}

export function deriveClientNutritionPlanSections(
  plans,
  pendingStatusByPlanId = {},
  language = "en",
) {
  const normalizedPendingStatusByPlanId = normalizePendingStatusMap(
    pendingStatusByPlanId,
  );

  const sections = (Array.isArray(plans) ? plans : []).reduce(
    (accumulator, plan) => {
      if (
        getPlanEffectiveStatus(plan, normalizedPendingStatusByPlanId) ===
        CLIENT_NUTRITION_STATUS_ARCHIVED
      ) {
        accumulator.archived.push(plan);
        return accumulator;
      }

      accumulator.active.push(plan);
      return accumulator;
    },
    { active: [], archived: [] },
  );

  sections.active.sort((leftPlan, rightPlan) => {
    const nameComparison = getNutritionPlanDisplayName(
      leftPlan,
      language,
    ).localeCompare(getNutritionPlanDisplayName(rightPlan, language), undefined, {
      sensitivity: "base",
    });
    if (nameComparison !== 0) {
      return nameComparison;
    }

    const leftCreatedAt = getComparableDateTimestamp(leftPlan?.createdAt);
    const rightCreatedAt = getComparableDateTimestamp(rightPlan?.createdAt);
    if (leftCreatedAt !== rightCreatedAt) {
      return leftCreatedAt - rightCreatedAt;
    }

    return String(leftPlan?.id ?? "").localeCompare(String(rightPlan?.id ?? ""), undefined, {
      sensitivity: "base",
    });
  });

  sections.archived = sortClientNutritionPlanListItems(
    sections.archived,
    normalizedPendingStatusByPlanId,
  );

  return sections;
}
